import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { useState } from 'react';
import {
  Question,
  Option,
  CreateQuestionInput,
  UpdateQuestionInput,
  OptionInput,
} from '../__generated__/graphql';
import { TAGS } from '../types/Tag';
import { QuestionFormStyles } from '../styles/QuestionFormStyles';
import RichTextEditor from './RichTextEditor';
import { useQuery } from '@apollo/client';
import { GET_ALL_LABELS } from '../graphql/getAllLabels';

interface QuestionFormProps {
  question?: Question;
  onSubmit: (questionInput: CreateQuestionInput | UpdateQuestionInput) => void;
}

const QuestionForm = ({ question, onSubmit }: QuestionFormProps) => {
  const { data: labelsData, loading: labelsLoading } = useQuery(GET_ALL_LABELS);

  const [prompt, setPrompt] = useState(question?.prompt || '');
  const [tag, setTag] = useState(question?.tag || '');
  const [label, setLabel] = useState(question?.label || '');
  const [optionA, setOptionA] = useState(question?.options[0]?.desc || '');
  const [optionB, setOptionB] = useState(question?.options[1]?.desc || '');
  const [optionC, setOptionC] = useState(question?.options[2]?.desc || '');
  const [optionD, setOptionD] = useState(question?.options[3]?.desc || '');
  const [explanation, setExplanation] = useState(question?.explanation || '');
  const [correctOptionIndex, setCorrectOptionIndex] = useState(
    question?.options.findIndex((option) => option.isAnswer) ?? 0,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!prompt.trim()) {
      newErrors.prompt = 'Question prompt is required';
    }
    if (!tag.trim()) {
      newErrors.tag = 'Category is required';
    }
    if (!optionA.trim()) {
      newErrors.optionA = 'Option A is required';
    }
    if (!optionB.trim()) {
      newErrors.optionB = 'Option B is required';
    }
    if (!optionC.trim()) {
      newErrors.optionC = 'Option C is required';
    }
    if (!optionD.trim()) {
      newErrors.optionD = 'Option D is required';
    }
    if (!explanation.trim()) {
      newErrors.explanation = 'Explanation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const options: Option[] = [
      { desc: optionA, isAnswer: correctOptionIndex === 0 },
      { desc: optionB, isAnswer: correctOptionIndex === 1 },
      { desc: optionC, isAnswer: correctOptionIndex === 2 },
      { desc: optionD, isAnswer: correctOptionIndex === 3 },
    ];

    if (question?.id) {
      const updateQuestionInput: UpdateQuestionInput = {
        explanation,
        prompt,
        tag,
        label: label || undefined,
        options: options as OptionInput[],
        id: question.id,
      };
      onSubmit(updateQuestionInput);
    } else {
      const createQuestionInput: CreateQuestionInput = {
        explanation,
        prompt,
        tag,
        label: label || undefined,
        options: options as OptionInput[],
      };

      onSubmit(createQuestionInput);
    }
  };

  return (
    <Box sx={QuestionFormStyles.formContainer}>
      {/* Category and Label Selection */}
      <Card sx={QuestionFormStyles.sectionCard}>
        <CardContent>
          <Typography variant="h6" sx={QuestionFormStyles.sectionTitle}>
            Question Classification
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ mt: 2 }}
          >
            <Autocomplete
              sx={QuestionFormStyles.autocompleteField}
              options={TAGS}
              value={tag}
              onChange={(_, newValue) => {
                setTag(newValue || '');
                if (errors.tag) {
                  setErrors((prev) => ({ ...prev, tag: '' }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Category"
                  placeholder="Select a category..."
                  error={!!errors.tag}
                  helperText={errors.tag}
                  required
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option}>
                  {option}
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
            <Autocomplete
              sx={QuestionFormStyles.autocompleteField}
              options={
                labelsData?.getAllLabels
                  ? [...labelsData.getAllLabels].sort()
                  : []
              }
              getOptionLabel={(option: string) => option}
              value={
                labelsData?.getAllLabels?.find((l: string) => l === label) ||
                null
              }
              onChange={(_, newValue) => setLabel(newValue || '')}
              loading={labelsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Label"
                  placeholder="Select a label..."
                />
              )}
              renderOption={(props, option: string) => (
                <Box component="li" {...props} key={option}>
                  {option}
                </Box>
              )}
              isOptionEqualToValue={(option: string, value: string) =>
                option === value
              }
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Question Prompt */}
      <Card sx={QuestionFormStyles.sectionCard}>
        <CardContent>
          <Typography variant="h6" sx={QuestionFormStyles.sectionTitle}>
            Question Prompt
          </Typography>
          <Box sx={{ mt: 2 }}>
            <RichTextEditor
              value={prompt}
              onChange={(value) => {
                setPrompt(value);
                if (errors.prompt) {
                  setErrors((prev) => ({ ...prev, prompt: '' }));
                }
              }}
            />
            {errors.prompt && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 1, display: 'block' }}
              >
                {errors.prompt}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Answer Options */}
      <Card sx={QuestionFormStyles.sectionCard}>
        <CardContent>
          <Typography variant="h6" sx={QuestionFormStyles.sectionTitle}>
            Answer Options
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={2}>
              {[
                {
                  label: 'Option A',
                  value: optionA,
                  setter: setOptionA,
                  letter: 'A',
                },
                {
                  label: 'Option B',
                  value: optionB,
                  setter: setOptionB,
                  letter: 'B',
                },
                {
                  label: 'Option C',
                  value: optionC,
                  setter: setOptionC,
                  letter: 'C',
                },
                {
                  label: 'Option D',
                  value: optionD,
                  setter: setOptionD,
                  letter: 'D',
                },
              ].map((option, index) => (
                <Paper
                  key={option.letter}
                  elevation={correctOptionIndex === index ? 3 : 1}
                  sx={{
                    ...QuestionFormStyles.optionPaper,
                    border:
                      correctOptionIndex === index
                        ? '2px solid #4caf50'
                        : '1px solid #e0e0e0',
                    backgroundColor:
                      correctOptionIndex === index ? '#f1f8e9' : '#ffffff',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={option.letter}
                      color={
                        correctOptionIndex === index ? 'success' : 'default'
                      }
                      sx={{ minWidth: 40 }}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={option.label}
                      value={option.value}
                      onChange={(event) => {
                        option.setter(event.target.value);
                        if (errors[`option${option.letter}`]) {
                          setErrors((prev) => ({
                            ...prev,
                            [`option${option.letter}`]: '',
                          }));
                        }
                      }}
                      sx={QuestionFormStyles.optionTextField}
                      error={!!errors[`option${option.letter}`]}
                      helperText={errors[`option${option.letter}`]}
                      required
                    />
                    <Radio
                      checked={correctOptionIndex === index}
                      onChange={() => setCorrectOptionIndex(index)}
                      color="success"
                    />
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Explanation */}
      <Card sx={QuestionFormStyles.sectionCard}>
        <CardContent>
          <Typography variant="h6" sx={QuestionFormStyles.sectionTitle}>
            Explanation
          </Typography>
          <Box sx={{ mt: 2 }}>
            <RichTextEditor
              value={explanation}
              onChange={(value) => {
                setExplanation(value);
                if (errors.explanation) {
                  setErrors((prev) => ({ ...prev, explanation: '' }));
                }
              }}
            />
            {errors.explanation && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 1, display: 'block' }}
              >
                {errors.explanation}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <Box sx={QuestionFormStyles.submitContainer}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          sx={QuestionFormStyles.submitButton}
        >
          {question ? 'Update Question' : 'Publish Question'}
        </Button>
      </Box>
    </Box>
  );
};

export default QuestionForm;
